'use client';
import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Product } from '@/models/product';
import { useToggle } from '@/hooks';
import {
  getCategoriesGraphQl,
  getKeyValue,
  isBrowser,
  getGiftingCategoriesGraphQl,
  pushGA4Event,
} from '@/utils/helper';
import { getProductsByCategoryId } from '@/features/ProductListing/services/getProducts';
import { usePathname, useRouter } from 'next/navigation';
import { searchParams } from '@/utils/helper';
import { PATHNAMES } from '@/features/Checkout/constants/delivery';
import { endpoints, logger } from '@/config';
import { http } from '@/config';
import { useCleverTap } from './clevertapContext';
import { CategoriesListProps } from '@/layouts/Header/components/HoverDropdown';
import {
  getWizzyPlpData,
  getWizzyTrendingSearches,
  wizzySearch,
} from '@/utils/wizzyHelper';
import { getMagentoCarouselsGraphQl } from '@/services';
import {
  DEFAULT_STORE_CODE,
  HEADER_FOOTER_TYPES,
  MAGENTO_GRAPHQL_CAROUSEL_TYPES,
  REVALIDATE_TIME,
} from '@/constants';
import { getCookie } from 'cookies-next';
import { GIFTING_BUILD_YOUR_OWN_BOX_PATH_STEPS } from '@/constants';
import { normalizePath } from '@/utils/helper';

interface PropTypes {
  children: ReactNode;
}

export type GiftingCategoriesListProps = {
  id: number;
  name: string;
  url_key: string;
  redirect_url: string;
  banner1_image: string;
  banner1_image_mobile: string;
};

interface useHeaderContextPropTypes {
  query: string;
  popularSearches: string[];
  trendingProducts: Product[];
  mostLovedRecipes: Product[];
  mostViewedStories: Product[];
  upcomingWorkshops: Product[];
  categoriesToShowOnHover: CategoriesListProps[];
  autoCompleteProducts: Product[];
  autoCompleteRecipes: Product[];
  autoCompleteArticles: Product[];
  tickerClosed: boolean;
  isNavLinksHidden: boolean;
  shouldColorChange: boolean;
  showHoverDropdown: boolean;
  pathNameArr: keyof typeof PATHNAMES;
  isSearchDrawerOpen: boolean;
  loading: boolean;
  showNoProductsFound: boolean | null;
  autoCompleteResponseId: string | null;
  searchBarRef: React.RefObject<HTMLInputElement | null>;
  searchAutoCompleteCategories: any[];
  hoverCategory: 'shop' | 'gifting' | null;
  shopCategories: CategoriesListProps[];
  giftingCategories: CategoriesListProps[];
  shopByLifestyleCategories: GiftingCategoriesListProps[];
  headerType: keyof typeof HEADER_FOOTER_TYPES;
  hideFooterOnly: boolean;
  deepSearch: (event: React.FormEvent<HTMLFormElement>) => void;
  setQuery: (query: string) => void;
  toggleHoverDropdown: (showHoverDropdown: boolean) => void;
  closeTicker: (tickerClosed?: boolean) => void;
  toggleIsSearchDrawerOpen: (searchFocus?: boolean) => void;
  fetchAutoCompleteRequest: (searchQuery: string | null) => void;
  setisNavLinksHidden: (value: boolean) => void;
  setShowNoProductsFound: Dispatch<SetStateAction<boolean | null>>;
  setHoverCategory: Dispatch<SetStateAction<'shop' | 'gifting' | null>>;
  setShopByLifestyleCategories: Dispatch<
    SetStateAction<GiftingCategoriesListProps[]>
  >;
}

const Context = createContext<any | null>(null);

const HeaderContextProvider: FC<PropTypes> = ({ children }) => {
  // Constants
  const pathname: string = usePathname();
  const router = useRouter();

  const { pushEvent } = useCleverTap();
  const splitPaths: string[] = usePathname().split('/');
  const pathNameArr = splitPaths[1] as keyof typeof PATHNAMES;

  // Header on Scroll States
  const prevScrollY = useRef(0);
  const searchBarRef = useRef<HTMLInputElement | null>(null);
  const [isNavLinksHidden, setisNavLinksHidden] = useState(false);
  const { value: isThresholdCrossed, toggleValue: toggleThreshold } =
    useToggle(false);
  // Search States
  const [query, setQuery] = useState<string>('');
  const [popularSearches, setPopularSearches] = useState<string[]>([]);
  const [trendingProducts, setTrendingProducts] = useState<Product[]>([]);
  const [autoCompleteProducts, setAutoCompleteProducts] = useState<any[]>([]);
  const [searchAutoCompleteCategories, setSearchAutoCompleteCategories] =
    useState<any[]>([]);
  const [autoCompleteRecipes, setAutoCompleteRecipes] = useState<Product[]>([]);
  const [autoCompleteArticles, setAutoCompleteArticles] = useState<Product[]>(
    []
  );
  const [autoCompleteResponseId, setAutoCompleteResponseId] = useState<
    string | null
  >(null);
  const [showNoProductsFound, setShowNoProductsFound] = useState<
    boolean | null
  >(null);
  // States for magento fetched items
  const [mostLovedRecipes, setMostLovedRecipes] = useState<Product[]>([]);
  const [mostViewedStories, setMostViewedStories] = useState<Product[]>([]);
  const [upcomingWorkshops, setUpcomingWorkshops] = useState<Product[]>([]);
  const [shopCategories, setShopCategories] = useState<CategoriesListProps[]>(
    []
  );
  const [giftingCategories, setGiftingCategories] = useState<
    CategoriesListProps[]
  >([]);
  const [categoriesToShowOnHover, setCategoriesToShowOnHover] = useState<
    CategoriesListProps[]
  >([]);
  const [hoverCategory, setHoverCategory] = useState<'shop' | 'gifting' | null>(
    null
  );
  const [shopByLifestyleCategories, setShopByLifestyleCategories] = useState<
    GiftingCategoriesListProps[]
  >([]);

  // Other States
  const { value: tickerClosed, toggleValue: closeTicker } = useToggle(true);
  const { value: isSearchDrawerOpen, toggleValue: toggleIsSearchDrawerOpen } =
    useToggle(false);
  const { value: showHoverDropdown, toggleValue: toggleshowHoverDropdown } =
    useToggle(false);
  const { value: loading, toggleValue: setLoading } = useToggle(false);

  const headerType: keyof typeof HEADER_FOOTER_TYPES = useMemo(
    () =>
      pathname.includes('/checkout')
        ? 'checkout'
        : pathname.includes('/cafe-menu')
          ? 'hidden'
          : 'global',
    [pathname]
  );

  const hideFooterOnly: boolean = useMemo(
    () =>
      GIFTING_BUILD_YOUR_OWN_BOX_PATH_STEPS.some(
        path => normalizePath(path) === normalizePath(pathname)
      ),
    [pathname]
  );

  // Functions
  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams();
      params.set(name, value);
      return params.toString();
    },
    [searchParams]
  );

  const hasTransparentHeader = useMemo(() => {
    if (pathname === '/') return true;
    if (
      pathname === '/the-ramadan-edit' ||
      pathname.startsWith('/the-ramadan-edit/')
    )
      return true;
    return false;
  }, [pathname]);

  const shouldColorChange = useMemo(() => {
    const notTransparentHeader = !hasTransparentHeader;
    return (
      notTransparentHeader || isThresholdCrossed || isSearchDrawerOpen || false
    );
  }, [hasTransparentHeader, isThresholdCrossed, isSearchDrawerOpen]);

  const onScroll = () => {
    const position = isBrowser ? window.scrollY : 0;
    if (position > 80) {
      toggleThreshold(true);
    } else {
      toggleThreshold(false);
    }
    if (Math.abs(position - prevScrollY.current) > 8 * 16) {
      setisNavLinksHidden(position > prevScrollY.current);
      prevScrollY.current = position;
    }
  };

  const deepSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const queryValue: any = query;
    if (queryValue && queryValue !== '') {
      // GA4 Discovery: search_query
      pushGA4Event('search_query', { search_term: queryValue });
      router.push(
        '/search-results' + '?' + createQueryString('query', queryValue)
      );
      const activeElement = document?.activeElement as HTMLElement;
      if (activeElement?.blur) {
        activeElement.blur();
      }
      document?.body?.focus();
      toggleIsSearchDrawerOpen(false);
    }
  };

  const fetchAutoCompleteRequest = useCallback(
    async (searchQuery: string | null) => {
      const clevertapModule = await import('clevertap-web-sdk');
      const xApiKey =
        (await getCookie('wizzy_api_key')) || process.env.WIZZY_API_KEY;
      const xStoreId =
        (await getCookie('wizzy_store_id')) || process.env.WIZZY_STORE_ID;
      const wizzySessionId = sessionStorage.getItem('wizzy_sessionId');
      const wizzyUserId = await getCookie('wizzy_userId');

      const headers: any = {
        'x-api-key': xApiKey,
        'x-store-id': xStoreId,
      };
      if (wizzySessionId) {
        headers['x-wizzy-sessionId'] = wizzySessionId;
      }
      if (wizzyUserId) {
        headers['x-wizzy-userId'] = wizzyUserId;
      }

      try {
        setLoading(true);
        if (searchQuery && searchQuery !== '' && searchQuery?.length > 2) {
          const wizzyres = await wizzySearch({
            query: searchQuery,
            headers,
            searchType: 'autocomplete',
          });
          if (wizzyres?.products && wizzyres?.products?.length > 0) {
            setShowNoProductsFound(false);
          } else {
            setShowNoProductsFound(true);
            // GA4 Discovery: search_zero_results (dedupe per session per term)
            const lastTerm = sessionStorage.getItem(
              'ga4_last_search_zero_term'
            );
            if (lastTerm !== searchQuery) {
              pushGA4Event('search_zero_results', { search_term: searchQuery });
              pushEvent({
                event: 'search_zero_results',
                data: { search_term: searchQuery },
              });
              sessionStorage.setItem('ga4_last_search_zero_term', searchQuery);
            }
          }
          setAutoCompleteProducts(wizzyres?.products || []);
          setSearchAutoCompleteCategories(
            wizzyres?.autocompleteCategories || []
          );
          setAutoCompleteRecipes(wizzyres?.recipes || []);
          setAutoCompleteArticles(wizzyres?.stories || []);
          setAutoCompleteResponseId(wizzyres?.searchResponseId);
          const oosProductsLength =
            wizzyres?.products?.filter(
              (item: { stock_status?: string }) =>
                item?.stock_status === 'OUT_OF_STOCK'
            )?.length || 0;
          clevertapModule.event.push('Searched', {
            Keyword: searchQuery,
            'Number of Search Results': wizzyres?.totalCount?.products,
            'Products Shown Count': wizzyres?.products?.length,
            'Number of Out of Stock': oosProductsLength,
            Type: 'Auto-Complete',
            Path: pathname,
            Page: 1,
          });
        } else {
          setShowNoProductsFound(null);
        }
      } catch (e) {
        logger.log('Error in autocomplete search', e);
      } finally {
        setLoading(false);
      }
    },
    [pathname, pushEvent, setLoading]
  );

  async function getPopularSearches() {
    try {
      const wizzyTrendingSearches = await getWizzyTrendingSearches();
      if (wizzyTrendingSearches?.length > 0) {
        setPopularSearches(wizzyTrendingSearches);
      }
    } catch (error) {
      logger.log('Error while fetching popular searches', error);
    }
  }

  // Prefetch requests
  const getCategoriesList = async (parentCategory: 'shop' | 'gifting') => {
    if (!parentCategory) {
      return;
    }
    const id =
      parentCategory === 'shop'
        ? 3
        : Number(process.env.NEXT_PUBLIC_GIFTING_CATEGORY_ID) || 422;
    const ENDPOINT = endpoints.GRAPHQL_URI;
    const request = {
      query: getCategoriesGraphQl(id),
    };
    try {
      const { response, data } = await http.post(
        ENDPOINT,
        {
          ...request,
        },
        {
          headers: {
            Store: (await getCookie('store_code')) || DEFAULT_STORE_CODE,
          },
          next: {
            revalidate: REVALIDATE_TIME,
          },
        }
      );
      if (response?.ok) {
        if (data?.data?.categoryList?.length > 0) {
          if (parentCategory === 'shop') {
            setShopCategories(data?.data?.categoryList[0]?.children);
          }
          if (parentCategory === 'gifting') {
            setGiftingCategories(data?.data?.categoryList[0]?.children);
          }
        }
      }
    } catch (error: any) {
      logger.log(error?.message);
    }
  };

  const getShopByLifestyleCategoriesList = async () => {
    const ENDPOINT = endpoints.GRAPHQL_URI;
    const categoryId = process.env.SHOP_BY_LIFESTYLE_CATEGORY_ID || 2043;
    const request = {
      query: getGiftingCategoriesGraphQl(Number(categoryId)),
    };

    try {
      const { response, data } = await http.post(
        ENDPOINT,
        {
          ...request,
        },
        {
          headers: {
            Store: (await getCookie('store_code')) || DEFAULT_STORE_CODE,
          },
          next: {
            revalidate: REVALIDATE_TIME,
          },
        }
      );
      if (response?.ok) {
        if (data?.data?.categoryList?.length > 0) {
          setShopByLifestyleCategories(data?.data?.categoryList[0]?.children);
        }
      }
    } catch (error: any) {
      logger.log(error?.message);
    }
  };

  const getTrendingResponse = async () => {
    const trendingProductsCarousel = await getMagentoCarouselsGraphQl({
      carouselType: MAGENTO_GRAPHQL_CAROUSEL_TYPES.TRENDING_PRODUCTS,
      store_code: (await getCookie('store_code')) || DEFAULT_STORE_CODE,
      token: (await getCookie('token')) || undefined,
    });
    const urlKey = trendingProductsCarousel?.[0]?.category_url_key;
    if (urlKey) {
      const { products } = await getWizzyPlpData({
        urlKey: urlKey,
        inStock: [true],
        useNextCache: true,
      });
      setTrendingProducts(products);
    }
  };

  const getMostLovedRecipes = async () => {
    const mostLovedRecipesList = await getProductsByCategoryId({
      categoryId: process.env.MOST_LOVED_RECIPES_CATEGORY_ID,
    });
    setMostLovedRecipes(mostLovedRecipesList?.items);
  };

  const getMostViewedStories = async () => {
    const mostViewedStoriesList = await getProductsByCategoryId({
      categoryId: process.env.MOST_VIEWED_STORIES_CATEGORY_ID,
    });
    setMostViewedStories(mostViewedStoriesList?.items);
  };

  const getUpcomingWorkshops = async () => {
    const upcomingWorkshopsList = await getProductsByCategoryId({
      categoryId: process.env.UPCOMING_WORKSHOPS_CATEGORY_ID,
    });
    const filteredWorkshops = upcomingWorkshopsList?.items?.filter?.(
      (item: { custom_attributes: any }) => {
        // Get the event start date from custom_attributes
        const attributes = getKeyValue(
          item?.custom_attributes,
          'attribute_code'
        );
        const eventStartDate = new Date(attributes.event_start_date);

        // Check if event start date is past today
        if (eventStartDate > new Date()) {
          return true; // Keep this workshop
        }
        return false; // Filter out this workshop
      }
    );
    setUpcomingWorkshops(filteredWorkshops);
  };

  async function fetchAllInitialRequests() {
    setLoading(true);
    await Promise.all([
      getCategoriesList('shop'),
      getCategoriesList('gifting'),
      getShopByLifestyleCategoriesList(),
      getTrendingResponse(),
      getMostLovedRecipes(),
      getMostViewedStories(),
      getUpcomingWorkshops(),
      getPopularSearches(),
    ]);
    setLoading(false);
  }

  const toggleHoverDropdown = useCallback((value?: boolean) => {
    toggleshowHoverDropdown(value);
  }, []);

  // Side Effects
  // Initial
  useEffect(() => {
    onScroll();
    fetchAllInitialRequests();
    return () => {
      onScroll();
      fetchAllInitialRequests();
    };
  }, []);

  useEffect(() => {
    if (hoverCategory === 'gifting' && giftingCategories?.length > 0) {
      setCategoriesToShowOnHover(giftingCategories);
    } else if (hoverCategory === 'shop' && shopCategories?.length > 0) {
      setCategoriesToShowOnHover(shopCategories);
    } else {
      setCategoriesToShowOnHover([]);
    }
  }, [hoverCategory, shopCategories, giftingCategories]);

  useEffect(() => {
    if (!showHoverDropdown) {
      window.addEventListener('scroll', onScroll);
    } else {
      window.removeEventListener('scroll', onScroll);
    }
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [showHoverDropdown]);

  // Reset scroll threshold when navigating to transparent-header pages
  useEffect(() => {
    onScroll();
  }, [pathname]);

  // Ticker
  useEffect(() => {
    const isClosed = sessionStorage.getItem(`tickerClosed:${pathname}`);
    if (isClosed === 'true') {
      closeTicker(true);
    } else {
      closeTicker(false);
    }
    return () => {
      closeTicker();
    };
  }, [pathname, closeTicker]);

  // Focus & Hover
  useEffect(() => {
    if ((isSearchDrawerOpen || showHoverDropdown) && !isThresholdCrossed) {
      toggleThreshold(true);
    } else {
      onScroll();
    }
    if (
      headerType === HEADER_FOOTER_TYPES.hidden ||
      headerType === HEADER_FOOTER_TYPES.checkout
    ) {
      if (isSearchDrawerOpen) {
        toggleIsSearchDrawerOpen(false);
      }
      if (showHoverDropdown) {
        toggleshowHoverDropdown(false);
      }
    }
  }, [isSearchDrawerOpen, showHoverDropdown]);

  useEffect(() => {
    if (!isSearchDrawerOpen) {
      setShowNoProductsFound(null);
    }
  }, [isSearchDrawerOpen]);

  const value: useHeaderContextPropTypes = {
    query,
    loading,
    popularSearches,
    trendingProducts,
    autoCompleteProducts,
    autoCompleteRecipes,
    autoCompleteArticles,
    mostLovedRecipes,
    mostViewedStories,
    upcomingWorkshops,
    categoriesToShowOnHover,
    tickerClosed,
    isNavLinksHidden,
    shouldColorChange,
    showHoverDropdown,
    pathNameArr,
    isSearchDrawerOpen,
    showNoProductsFound,
    autoCompleteResponseId,
    searchBarRef,
    searchAutoCompleteCategories,
    hoverCategory,
    shopCategories,
    giftingCategories,
    shopByLifestyleCategories,
    headerType,
    hideFooterOnly,
    deepSearch,
    setQuery,
    toggleHoverDropdown,
    closeTicker,
    toggleIsSearchDrawerOpen,
    fetchAutoCompleteRequest,
    setisNavLinksHidden,
    setShowNoProductsFound,
    setHoverCategory,
    setShopByLifestyleCategories,
  };
  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useHeaderContext = (): useHeaderContextPropTypes =>
  useContext(Context);

export default HeaderContextProvider;
